import React, { useMemo, useState } from "react";
import { Mail, MailOpen, ChevronDown, ChevronRight, ArrowDownLeft, ArrowUpRight, Inbox, Reply } from "lucide-react";
import { sanitizeHtml } from "../lib/sanitize";

function fmtDate(s) {
  if (!s) return "";
  try {
    return new Date(s).toLocaleString();
  } catch {
    return s;
  }
}

// Normalize subject for fallback grouping: drop "Re:", "Fwd:", "RE:" etc + collapse whitespace
function normSubject(s) {
  if (!s) return "(no subject)";
  return s.replace(/^(?:\s*(?:re|fwd|fw)\s*:\s*)+/i, "").trim().toLowerCase() || "(no subject)";
}

/**
 * Groups email_sent + inbound_email activities into threads.
 * Primary key: payload.thread_id (Gmail threadId). Fallback: normalized subject.
 */
function groupThreads(activities) {
  const emails = (activities || []).filter(
    (a) => a.type === "email_sent" || a.type === "inbound_email"
  );
  if (emails.length === 0) return [];

  const byKey = new Map();
  for (const a of emails) {
    const tid = a.payload?.thread_id || a.payload?.gmail_thread_id;
    const key = tid ? `tid:${tid}` : `subj:${normSubject(a.payload?.subject)}`;
    if (!byKey.has(key)) byKey.set(key, []);
    byKey.get(key).push(a);
  }
  const threads = Array.from(byKey.entries()).map(([key, msgs]) => {
    msgs.sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime());
    const last = msgs[msgs.length - 1];
    const first = msgs[0];
    return {
      key,
      subject: first.payload?.subject || "(no subject)",
      messages: msgs,
      last_at: last.at,
      first_at: first.at,
      count: msgs.length,
      has_inbound: msgs.some((m) => m.type === "inbound_email"),
      has_outbound: msgs.some((m) => m.type === "email_sent"),
    };
  });
  // Most recent thread first
  threads.sort((a, b) => new Date(b.last_at).getTime() - new Date(a.last_at).getTime());
  return threads;
}

function Message({ msg }) {
  const isInbound = msg.type === "inbound_email";
  const from = isInbound
    ? `${msg.payload?.from_name ? msg.payload.from_name + " " : ""}<${msg.payload?.from || "?"}>`
    : `${msg.by_name || "You"}${msg.payload?.from ? ` <${msg.payload.from}>` : ""}`;
  const to = isInbound ? null : msg.payload?.to;
  const html = msg.payload?.html;
  const body = msg.payload?.body || msg.payload?.preview || "";
  return (
    <div
      data-testid={`thread-msg-${msg.id}`}
      className={`relative pl-4 py-3 rounded-md ${
        isInbound ? "bg-orange-50/40 border-l-2 border-orange-300" : "bg-slate-50/60 border-l-2 border-slate-300"
      }`}
    >
      <div className="flex items-center justify-between gap-3 mb-1.5">
        <div className="flex items-center gap-2 min-w-0">
          {isInbound ? (
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-orange-700 bg-orange-100 px-2 py-0.5 rounded-full">
              <ArrowDownLeft size={10} /> Received
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-slate-700 bg-slate-200 px-2 py-0.5 rounded-full">
              <ArrowUpRight size={10} /> Sent
            </span>
          )}
          <span className="text-xs text-slate-700 truncate font-medium">{from}</span>
        </div>
        <span className="text-[11px] text-slate-500 shrink-0">{fmtDate(msg.at)}</span>
      </div>
      {to && (
        <div className="text-[11px] text-slate-500 mb-1">To: <span className="text-slate-700">{to}</span></div>
      )}
      {html ? (
        <div className="text-sm text-slate-800 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: sanitizeHtml(html) }} />
      ) : (
        <div className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">{body}</div>
      )}
    </div>
  );
}

function Thread({ thread, defaultOpen, onReply }) {
  const [open, setOpen] = useState(defaultOpen);
  const Latest = thread.messages[thread.messages.length - 1];
  // Last inbound message gives us the best reply target; else fall back to last outbound (Re:Re:)
  const replyTarget = useMemo(() => {
    for (let i = thread.messages.length - 1; i >= 0; i--) {
      if (thread.messages[i].type === "inbound_email") return thread.messages[i];
    }
    return thread.messages[thread.messages.length - 1];
  }, [thread]);
  const handleReply = (e) => {
    e.stopPropagation();
    const p = replyTarget.payload || {};
    onReply?.({
      thread_id: p.thread_id || p.gmail_thread_id,
      message_id: p.message_id || p.gmail_message_id,
      subject: p.subject || thread.subject,
      from: p.from || (replyTarget.type === "email_sent" ? p.to : null),
      from_name: p.from_name,
      body: p.body || p.preview,
      html: p.html,
      at: replyTarget.at,
    });
  };
  return (
    <div
      data-testid={`email-thread-${thread.key}`}
      className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden"
    >
      <div className="w-full px-4 py-3 flex items-center justify-between gap-3 hover:bg-slate-50 transition-colors">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-3 min-w-0 flex-1 text-left"
          data-testid={`email-thread-toggle-${thread.key}`}
        >
          {thread.count > 1 ? (
            <MailOpen size={16} className="text-orange-500 shrink-0" />
          ) : (
            <Mail size={16} className="text-slate-500 shrink-0" />
          )}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-slate-900 truncate">{thread.subject}</span>
              <span className="text-[11px] font-medium px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 shrink-0">
                {thread.count}
              </span>
              {thread.has_inbound && thread.has_outbound && (
                <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700 shrink-0">
                  Two-way
                </span>
              )}
            </div>
            <div className="text-xs text-slate-500 mt-0.5 truncate">
              Last: {fmtDate(thread.last_at)} • {Latest.type === "inbound_email" ? "received" : "sent"}
            </div>
          </div>
          {open ? <ChevronDown size={14} className="text-slate-500 shrink-0" /> : <ChevronRight size={14} className="text-slate-500 shrink-0" />}
        </button>
        {onReply && (
          <button
            type="button"
            onClick={handleReply}
            data-testid={`email-thread-reply-${thread.key}`}
            className="shrink-0 inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 rounded-md border border-orange-200 bg-orange-50 text-orange-700 hover:bg-orange-100"
            title="Reply in this thread"
          >
            <Reply size={12} /> Reply
          </button>
        )}
      </div>
      {open && (
        <div className="border-t border-slate-100 p-3 space-y-2 bg-slate-50/30">
          {thread.messages.map((m) => (
            <Message key={m.id} msg={m} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function EmailThreadsView({ activities, onReply }) {
  const threads = useMemo(() => groupThreads(activities), [activities]);
  if (threads.length === 0) return null;
  return (
    <div className="space-y-3" data-testid="email-threads-view">
      <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
        <Inbox size={15} className="text-orange-500" />
        Email conversations
        <span className="text-[11px] font-medium px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600">
          {threads.length}
        </span>
      </div>
      {threads.map((t, i) => (
        <Thread key={t.key} thread={t} defaultOpen={i === 0} onReply={onReply} />
      ))}
    </div>
  );
}
